import React, { ChangeEvent, useCallback, useRef, useState } from "react";
import { FileIcon, Loader2, PaperclipIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { useSession } from "@clerk/clerk-react";
import { getUnexpiredToken } from "@/utils/utils";
import { UploadedFile } from "@/utils/types";

interface ApiResponse {
  message?: string;
  url?: string;
  error?: string;
}

// Default configuration
const DEFAULT_MAX_FILE_SIZE_MB = 30;
const DEFAULT_MAX_FILES = 20;
const DEFAULT_SUPPORTED_FILE_TYPES = [
  "application/pdf",
  // Only pdfs supported as non-image files for api
  // https://docs.anthropic.com/en/docs/build-with-claude/pdf-support
  // Maybe more in future? Their website allows other file types :s
  // https://support.anthropic.com/en/articles/8241126-what-kinds-of-documents-can-i-upload-to-claude-ai
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/bmp",
  "image/tiff",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
];

type UseImageUploaderOptions = {
  maxFileSizeMb?: number;
  maxFiles?: number;
  acceptedMimeTypes?: string[];
  onUploadSuccess?: (file: UploadedFile) => void;
};

type UseImageUploaderReturnType = Readonly<{
  uploadFiles: (files: File[]) => void;
  uploadedFiles: UploadedFile[];
  isUploading: boolean;
  triggerUpload: () => void;
  clearUploadedFiles: () => void;
  FilePreviewerComponent: () => JSX.Element | null;
  FileUploadComponent: ({
    className,
    fileUploadCallback,
    buttonLabel,
  }: {
    className?: string;
    fileUploadCallback?: () => void;
    buttonLabel?: React.ReactNode;
  }) => JSX.Element;
  FilePreviewItem: ({
    file,
    onRemove,
  }: {
    file: UploadedFile;
    onRemove?: () => void;
  }) => JSX.Element;
}>;

export function useImageUploader(
  options?: UseImageUploaderOptions
): UseImageUploaderReturnType {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null); // Added ref for programmatic click

  const { session } = useSession();

  const maxFileSizeMb = options?.maxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB;
  const maxFiles = options?.maxFiles ?? DEFAULT_MAX_FILES;
  const supportedFileTypes =
    options?.acceptedMimeTypes ?? DEFAULT_SUPPORTED_FILE_TYPES;
  const onUploadSuccessCallback = options?.onUploadSuccess;

  // Function to trigger file input click
  const triggerUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const uploadFiles = async (files: File[]) => {
    // Check max files based on config
    if (uploadedFiles.length + files.length > maxFiles) {
      toast.error(
        `Maximum ${maxFiles} file${maxFiles !== 1 ? "s" : ""} allowed`
      );
      return;
    }

    setIsUploading(true);

    let successfulUploads: UploadedFile[] = [];

    for (const file of files) {
      // Use configured supported types
      if (!supportedFileTypes.includes(file.type)) {
        toast.error(`Unsupported file type: ${file.name}`);
        continue; // Skip this file, maybe allow others? Or return early? Let's skip.
      }

      if (file.size > maxFileSizeMb * 1024 * 1024) {
        toast.error(`File ${file.name} exceeds ${maxFileSizeMb}MB limit`);
        continue;
      }
    }

    const validFiles = files.filter(
      (file) =>
        supportedFileTypes.includes(file.type) &&
        file.size <= maxFileSizeMb * 1024 * 1024
    );

    if (validFiles.length === 0 && files.length > 0) {
      setIsUploading(false);
      return;
    }

    for (const file of validFiles) {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      try {
        const base64data = await new Promise<string>((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
        });

        const supabaseJWT = await getUnexpiredToken(session);

        const response = await fetch(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/imageUpload`,
          {
            method: "POST",
            body: JSON.stringify({
              file: base64data.split(",")[1],
              fileName: file.name,
              mimeType: file.type,
            }),
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        );
        const data = (await response.json()) as ApiResponse;

        if (!response.ok || data.error) {
          toast.error(data.error || `Failed to upload ${file.name}`);
        }

        if (data.url) {
          const fileType = file.type.startsWith("image/") ? "image" : "file";
          const newFile: UploadedFile = {
            url: data.url!,
            name: file.name,
            type: fileType,
            mimeType: file.type,
          };
          successfulUploads.push(newFile);
          if (onUploadSuccessCallback) {
            onUploadSuccessCallback(newFile);
          }
        } else {
          throw new Error(
            `Image URL not received after uploading ${file.name}.`
          );
        }
      } catch (error: any) {
        console.error(error);
        toast.error(error.message || `Error uploading ${file.name}`);
      }
    }

    if (successfulUploads.length > 0) {
      setUploadedFiles((prev) => [...prev, ...successfulUploads]);
    }

    setIsUploading(false);
  };

  const clearUploadedFiles = () => {
    setUploadedFiles([]);
  };

  const FilePreviewItem = ({
    file,
    onRemove,
  }: {
    file: UploadedFile;
    onRemove: () => void;
  }) => {
    return (
      <div className="relative w-fit rounded-md  mt-2">
        {onRemove && (
          <X
            className="absolute cursor-pointer -top-1 -right-1.5 z-10 hover:text-icons-primary text-icons-secondary bg-background-secondary hover:bg-background-accent rounded-full p-0.5 size-4"
            onClick={onRemove}
          />
        )}
        <div className="h-16 w-16 rounded-md relative flex items-center justify-center bg-background-secondary p-1">
          {file.type === "image" ? (
            <img
              src={file.url}
              alt="Uploaded"
              height={100}
              width={100}
              className="h-10 w-16 rounded-sm relative flex items-center justify-center object-contain"
            />
          ) : file.mimeType === "application/pdf" ? (
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full h-full"
            >
              <div className="flex flex-col items-center justify-center w-full h-full">
                <FileIcon className="size-4 text-icons-primary" />
                <span className="text-xs truncate max-w-full">PDF</span>
              </div>
            </a>
          ) : (
            <div className="flex flex-col items-center justify-center w-full h-full">
              <FileIcon className="size-4 text-icons-primary" />
              <span className="text-xs truncate max-w-full">File</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const FilePreviewerComponent = useCallback(() => {
    if (!uploadedFiles.length && !isUploading) return null;

    return (
      <div className="flex flex-wrap gap-2 mb-2">
        {uploadedFiles.map((file, index) => (
          <FilePreviewItem
            key={index}
            file={file}
            onRemove={() =>
              setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
            }
          />
        ))}
        {isUploading && (
          <div className="h-16 w-16 bg-background-secondary rounded-sm flex items-center justify-center">
            <Loader2 className="animate-spin text-icons-primary" />
          </div>
        )}
      </div>
    );
  }, [isUploading, uploadedFiles]);

  const FileUploadComponent = useCallback(
    ({
      className,
      fileUploadCallback,
      buttonLabel,
    }: {
      className?: string;
      fileUploadCallback?: () => void;
      buttonLabel?: React.ReactNode;
    }) => {
      const uploadClick = () => triggerUpload(); // Use the exposed trigger

      const disabled = isUploading;

      function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
          uploadFiles(files);
          fileUploadCallback?.();
        }
        if (e.target) {
          e.target.value = "";
        }
      }

      return (
        <React.Fragment>
          <Button
            variant="ghost"
            className={cn(
              "h-auto flex gap-2 py-1 px-2 items-center rounded justify-start group text-xs text-secondary-n-50",
              className
            )}
            disabled={disabled}
            onClick={uploadClick}
          >
            <PaperclipIcon
              className={cn(
                "cursor-pointer size-4 text-icons-muted group-hover:text-icons-primary outline-none",
                disabled && "cursor-default opacity-75"
              )}
              data-tooltip-content={disabled ? "" : "Upload files"}
              data-tooltip-id="tooltip"
            />
            {buttonLabel || "Upload a file"}
          </Button>
          <input
            type="file"
            multiple={maxFiles !== 1} // Set multiple based on config
            ref={fileInputRef}
            className="hidden"
            disabled={disabled}
            onChange={handleFileChange}
            accept={supportedFileTypes.join(",")}
          />
        </React.Fragment>
      );
    },
    [isUploading, triggerUpload, maxFiles, supportedFileTypes]
  );

  return {
    uploadFiles,
    uploadedFiles,
    isUploading,
    triggerUpload,
    clearUploadedFiles,
    FilePreviewerComponent,
    FileUploadComponent,
    FilePreviewItem,
  };
}
